import type { BookableCandidateDto } from "@repo/api";
import type { PatientDto, ProtocolBookingDto } from "@repo/data-store";
import { bookProtocolVisit, usePatients, useProtocolBookings } from "@repo/data-store";
import { mintEntityId } from "@repo/domain";
import { format, parseISO } from "date-fns";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useFindProtocolVisitCandidates } from "@/features/scheduling/hooks/use-find-bookable-candidates";
import { combineDateTimeISO, type TimeSlot } from "@/features/scheduling/lib/appointment-utils";
import { useAppForm } from "@/shared/forms/app-form";
import { createSubmitOnce } from "@/shared/forms/submit-once";

/**
 * NO PRACTITIONER FIELD. A protocol visit is delivered by the patient's care
 * manager, and the server reads that off the active case — so offering a picker
 * was offering a choice the booking would not honour. It also let the UI search
 * one clinician's calendar while the booking reserved another's.
 */
export interface ProtocolsBookingFormValues {
  patientId: string;
  date: Date | null;
  startTime: string;
  endTime: string;
  protocol: ProtocolBookingDto | null;
}

export const DEFAULT_PROTOCOLS_BOOKING_FORM_VALUES: ProtocolsBookingFormValues = {
  patientId: "",
  date: null,
  startTime: "",
  endTime: "",
  protocol: null,
};

export const protocolsBookingFormSchema = z.object({
  patientId: z.string().min(1, "Patient is required"),
  date: z.date().nullable(),
  startTime: z.string().min(1, "Valid time required"),
  endTime: z.string().min(1, "Valid time required"),
  protocol: z
    .object({
      canonical: z.string().min(1),
      protocolUrl: z.string().url(),
      protocolVersion: z.string().min(1),
      title: z.string().min(1),
      description: z.string().optional(),
      durationMinutes: z.number().positive(),
      examUrls: z.array(z.string().url()),
      steps: z.array(
        z.object({
          orders: z.array(
            z.object({
              definitionCanonical: z.string().regex(/^https?:\/\/[^|]+\|[^|]+$/),
              orderId: z.string().min(1),
              sourceOccurrenceId: z.string().min(1),
            }),
          ),
        }),
      ),
    })
    .nullable()
    .refine((protocol) => protocol !== null, { message: "A protocol revision is required" }),
});

export interface UseProtocolsBookingFormOptions {
  initialDate?: Date;
  initialTime?: string;
  onSuccess?: () => void;
}

export interface UseProtocolsBookingFormReturn {
  patients: PatientDto[];
  protocolOptions: ProtocolBookingDto[];
  isLoadingPatients: boolean;
  isPending: boolean;
  defaultValues: ProtocolsBookingFormValues;
  validators: { onDynamic: typeof protocolsBookingFormSchema };
  handleSubmit: (value: ProtocolsBookingFormValues) => Promise<void>;
  /** One slot per candidate — a candidate IS a bookable start, already on the grid. */
  slots: TimeSlot[];
  isLoadingAvailability: boolean;
  availabilityError: string | null;
  queryCandidates: ReturnType<typeof useFindProtocolVisitCandidates>["query"];
  resetCandidates: ReturnType<typeof useFindProtocolVisitCandidates>["reset"];
}

/**
 * A candidate is already exactly one bookable start of the visit's own length,
 * chosen on the clinic's 15-minute grid. The old flow received wide availability
 * WINDOWS and subdivided them client-side, which meant the UI re-derived start
 * times the server had already decided — and could offer one the server would
 * then refuse.
 */
function candidateToSlot(candidate: BookableCandidateDto): TimeSlot {
  const start = parseISO(candidate.start);
  const end = parseISO(candidate.end);
  return {
    startTime: candidate.start,
    endTime: candidate.end,
    dateDisplay: format(start, "EEE, MMM d"),
    display: format(start, "h:mm a"),
    endDisplay: format(end, "h:mm a"),
  };
}

export function useProtocolsBookingFormData(options: UseProtocolsBookingFormOptions): UseProtocolsBookingFormReturn {
  const { initialDate, initialTime, onSuccess } = options;

  const { patients, isLoading: isLoadingPatients } = usePatients();
  const protocolOptions = useProtocolBookings();
  const [isPending, setIsPending] = useState(false);
  const [once] = useState(() => createSubmitOnce(() => ({ id: mintEntityId(), orderSetId: mintEntityId() })));
  const {
    candidates,
    isLoading: isLoadingAvailability,
    error: availabilityError,
    query: queryCandidates,
    reset: resetCandidates,
  } = useFindProtocolVisitCandidates();

  const defaultValues: ProtocolsBookingFormValues = {
    ...DEFAULT_PROTOCOLS_BOOKING_FORM_VALUES,
    date: initialDate ?? null,
    startTime: initialTime && initialDate ? combineDateTimeISO(initialDate, initialTime) : "",
    endTime: "",
  };

  const slots = useMemo(() => candidates.map(({ candidate }) => candidateToSlot(candidate)), [candidates]);

  const handleSubmit = async (value: ProtocolsBookingFormValues) => {
    if (!value.date || !value.protocol || !value.startTime) return;
    const availability = candidates.find(({ candidate }) => candidate.start === value.startTime);
    if (!availability) {
      toast.error("Selected slot is no longer available — please pick another time.");
      return;
    }
    const protocol = value.protocol;
    setIsPending(true);
    try {
      await once(async (identity) => {
        await bookProtocolVisit({
          ...identity,
          candidate: availability.candidate,
          currentCaseId: availability.currentCaseId,
          protocol,
          protocolUrl: protocol.protocolUrl,
          protocolVersion: protocol.protocolVersion,
          patientId: value.patientId,
        }).isPersisted.promise;
        onSuccess?.();
      });
    } catch {
    } finally {
      setIsPending(false);
    }
  };

  return {
    patients: patients ?? [],
    protocolOptions,
    isLoadingPatients,
    isPending,
    defaultValues,
    validators: { onDynamic: protocolsBookingFormSchema },
    handleSubmit,
    slots,
    isLoadingAvailability,
    availabilityError,
    queryCandidates,
    resetCandidates,
  };
}

export { useAppForm };
